import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Instagram, Loader2, Plus, Trash2, Copy, CheckCircle2, AlertCircle, Zap, Power, Link2 } from "lucide-react";
import { API, authHeaders, formatApiError } from "./AdminPage";
import { useAdminT } from "./adminI18n";

export const AutomationTab = () => {
  const { t } = useAdminT();
  const [status, setStatus] = useState(null);
  const [rules, setRules] = useState(null);
  const [activity, setActivity] = useState([]);
  const [keywords, setKeywords] = useState("");
  const [reply, setReply] = useState("");
  const [saving, setSaving] = useState(false);
  const [bossLink, setBossLink] = useState("");
  const [bossBusy, setBossBusy] = useState(false);

  const makeBossLink = async () => {
    setBossBusy(true);
    try {
      const res = await axios.post(`${API}/admin/ig/connect-link`, {}, { headers: authHeaders() });
      setBossLink(res.data.url);
      toast.success(t("تم إنشاء الرابط — أرسليه لمن يدير الحساب"));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBossBusy(false);
    }
  };

  const load = () => {
    axios.get(`${API}/admin/ig/status`, { headers: authHeaders() }).then((res) => setStatus(res.data)).catch(() => {});
    axios.get(`${API}/admin/ig/rules`, { headers: authHeaders() }).then((res) => setRules(Array.isArray(res.data) ? res.data : [])).catch(() => setRules([]));
    axios.get(`${API}/admin/ig/activity`, { headers: authHeaders() }).then((res) => setActivity(Array.isArray(res.data) ? res.data : [])).catch(() => {});
  };

  useEffect(() => {
    load();
    const params = new URLSearchParams(window.location.search);
    if (params.get("ig") === "connected") toast.success(t("تم ربط إنستغرام بنجاح"));
    if (params.get("ig") === "error") toast.error(t("تعذر ربط إنستغرام — جرّبي مجدداً"));
  }, [t]);

  const copy = (text, id) => {
    navigator.clipboard.writeText(text).then(() => toast.success(t("تم النسخ")));
  };

  const connect = () => {
    const token = localStorage.getItem("themaya_token") || "";
    window.location.href = `${API}/auth/meta/start?auth=${encodeURIComponent(token)}`;
  };

  const addRule = async () => {
    const kws = keywords.split(/[,،]/).map((k) => k.trim()).filter(Boolean);
    if (!kws.length || !reply.trim()) {
      toast.error(t("أدخلي الكلمات المفتاحية ونص الرد"));
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/admin/ig/rules`, { keywords: kws, reply_text: reply.trim(), enabled: true }, { headers: authHeaders() });
      toast.success(t("تمت إضافة القاعدة"));
      setKeywords("");
      setReply("");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const toggleRule = async (r) => {
    try {
      await axios.put(`${API}/admin/ig/rules/${r.id}`, { keywords: r.keywords, reply_text: r.reply_text, dm_text: r.dm_text || "", enabled: !r.enabled }, { headers: authHeaders() });
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const deleteRule = async (r) => {
    try {
      await axios.delete(`${API}/admin/ig/rules/${r.id}`, { headers: authHeaders() });
      toast.success(t("تم حذف القاعدة"));
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const actionLabel = (a) =>
    ({ replied: t("تم الرد"), matched_not_connected: t("تطابق — بانتظار الربط"), no_match: t("بدون تطابق"), skipped_nested: t("تعليق فرعي"), error: t("خطأ") }[a] || a);

  const actionColor = (a) =>
    a === "replied" ? "text-green-400" : a === "error" ? "text-red-400" : a === "no_match" ? "text-luxury-gray" : "text-luxury-gold";

  return (
    <div data-testid="automation-tab" className="space-y-8">
      <section className="bg-luxury-surface border border-luxury-border p-6 rounded-2xl">
        <div className="flex items-center gap-3 mb-4">
          <Instagram className="w-5 h-5 text-luxury-gold" strokeWidth={1.5} />
          <h3 className="text-luxury-bone font-semibold">{t("ربط إنستغرام")}</h3>
        </div>
        {!status ? (
          <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 text-luxury-gold animate-spin" /></div>
        ) : status.connected ? (
          <div data-testid="ig-connected" className="flex items-center gap-3 text-green-400 text-sm">
            <CheckCircle2 className="w-5 h-5" strokeWidth={1.5} />
            {t("متصل بحساب")} <span dir="ltr" className="font-semibold">@{status.connection?.ig_username}</span>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-luxury-gray text-sm">
              <AlertCircle className="w-4 h-4 text-luxury-gold" strokeWidth={1.5} />
              {status.configured ? t("جاهز للربط — اضغطي الزر وسجّلي دخولك بإنستغرام") : t("بانتظار إعداد مفاتيح Meta على الخادم — أرسلي لي «مفاتيحي جاهزة» عندما تنهين الخطوات")}
            </div>
            {status.configured && (
              <button data-testid="ig-connect-btn" onClick={connect}
                className="flex items-center gap-2 bg-luxury-gold text-luxury-black font-semibold px-6 py-3 rounded-full hover:bg-luxury-goldLight transition-colors text-sm">
                <Instagram className="w-4 h-4" strokeWidth={1.5} /> {t("ربط حساب إنستغرام")}
              </button>
            )}
            <div className="bg-luxury-black border border-luxury-border rounded-xl p-4">
              <p className="text-luxury-bone text-sm font-semibold mb-1">{t("الحساب يديره شخص آخر؟")}</p>
              <p className="text-luxury-gray text-xs mb-3">{t("أنشئي رابطاً آمناً يُستخدم مرة واحدة وأرسليه له — يفتحه، يسجّل دخوله بحساب فيسبوك المرتبط بالصفحة، ويضغط سماح. بدون مشاركة كلمة مرورك أبداً.")}</p>
              {bossLink ? (
                <div className="flex items-center gap-2">
                  <code dir="ltr" data-testid="boss-link" className="text-luxury-gold bg-luxury-surface px-2 py-1.5 rounded text-[11px] break-all flex-1">{bossLink}</code>
                  <button data-testid="boss-link-copy" onClick={() => copy(bossLink)} className="text-luxury-gray hover:text-luxury-gold transition-colors flex-shrink-0"><Copy className="w-4 h-4" strokeWidth={1.5} /></button>
                </div>
              ) : (
                <button data-testid="boss-link-btn" onClick={makeBossLink} disabled={bossBusy || !status.configured}
                  className="flex items-center gap-2 border border-luxury-gold/60 text-luxury-gold px-4 py-2 rounded-full text-xs hover:bg-luxury-gold hover:text-luxury-black transition-colors disabled:opacity-50">
                  {bossBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Link2 className="w-3.5 h-3.5" strokeWidth={1.5} />} {t("أنشئي رابط الربط للمدير")}
                </button>
              )}
            </div>
            <div className="bg-luxury-black border border-luxury-border rounded-xl p-4 space-y-3 text-xs">
              <p className="text-luxury-bone font-semibold">{t("بيانات الربط (تلصقينها في لوحة Meta)")}</p>
              {[["Webhook URL", status.webhook_url], ["Verify Token", status.verify_token], ["Redirect URI", status.redirect_uri]].map(([label, val], i) => (
                <div key={label} className="flex items-center gap-2 flex-wrap">
                  <span className="text-luxury-gray w-24 flex-shrink-0">{label}</span>
                  <code dir="ltr" data-testid={`ig-copy-${i}`} className="text-luxury-gold bg-luxury-surface px-2 py-1 rounded text-[11px] break-all flex-1">{val}</code>
                  <button onClick={() => copy(val, i)} className="text-luxury-gray hover:text-luxury-gold transition-colors"><Copy className="w-3.5 h-3.5" strokeWidth={1.5} /></button>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>

      <section className="bg-luxury-surface border border-luxury-border p-6 rounded-2xl">
        <div className="flex items-center gap-3 mb-1">
          <Zap className="w-5 h-5 text-luxury-gold" strokeWidth={1.5} />
          <h3 className="text-luxury-bone font-semibold">{t("قواعد الرد التلقائي")}</h3>
        </div>
        <p className="text-luxury-gray text-xs mb-5">{t("عندما يكتب أحد تعليقاً يحتوي كلمة مفتاحية، يرد النظام تلقائياً بالرد الذي تحددينه")}</p>

        <div className="bg-luxury-black border border-luxury-border rounded-xl p-4 mb-5 space-y-3">
          <input data-testid="rule-keywords-input" value={keywords} onChange={(e) => setKeywords(e.target.value)}
            placeholder={t("الكلمات المفتاحية مفصولة بفاصلة — مثال: sunset، سانسيت، سانست")}
            className="w-full bg-luxury-surface border border-luxury-border rounded-lg px-4 py-2.5 text-sm text-luxury-bone focus:outline-none focus:border-luxury-gold" />
          <textarea data-testid="rule-reply-input" value={reply} onChange={(e) => setReply(e.target.value)} rows={2}
            placeholder={t("الرد التلقائي — مثال: أهلاً فيكِ 🤍 هذا رابط التسجيل: themaya.online/p/sunset")}
            className="w-full bg-luxury-surface border border-luxury-border rounded-lg px-4 py-2.5 text-sm text-luxury-bone focus:outline-none focus:border-luxury-gold resize-none" />
          <button data-testid="rule-add-btn" onClick={addRule} disabled={saving}
            className="flex items-center gap-2 bg-luxury-gold text-luxury-black text-sm font-semibold px-5 py-2.5 rounded-full hover:bg-luxury-goldLight transition-colors disabled:opacity-50">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" strokeWidth={1.5} />} {t("أضيفي القاعدة")}
          </button>
        </div>

        {rules === null ? (
          <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 text-luxury-gold animate-spin" /></div>
        ) : rules.length === 0 ? (
          <p className="text-luxury-gray text-sm text-center py-4">{t("لا قواعد بعد — أضيفي أول قاعدة فوق")}</p>
        ) : (
          <div className="space-y-3">
            {rules.map((r) => (
              <div key={r.id} data-testid={`rule-${r.id.slice(0, 8)}`} className="bg-luxury-black border border-luxury-border rounded-xl p-4">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-2 flex-wrap">
                    {r.keywords.map((k) => (
                      <span key={k} className="text-xs bg-luxury-surface border border-luxury-gold/40 text-luxury-gold px-2.5 py-1 rounded-full">{k}</span>
                    ))}
                  </div>
                  <div className="flex items-center gap-1">
                    <button data-testid={`rule-toggle-${r.id.slice(0, 8)}`} onClick={() => toggleRule(r)} title={r.enabled ? t("مفعّلة") : t("متوقفة")}
                      className={`p-2 transition-colors ${r.enabled ? "text-green-400" : "text-luxury-gray"}`}>
                      <Power className="w-4 h-4" strokeWidth={1.5} />
                    </button>
                    <button data-testid={`rule-delete-${r.id.slice(0, 8)}`} onClick={() => deleteRule(r)} className="p-2 text-luxury-gray hover:text-red-400 transition-colors">
                      <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                    </button>
                  </div>
                </div>
                <p className="text-luxury-bone text-sm mt-2">{r.reply_text}</p>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="bg-luxury-surface border border-luxury-border p-6 rounded-2xl">
        <h3 className="text-luxury-bone font-semibold mb-4">{t("سجل التعليقات الأخير")}</h3>
        {activity.length === 0 ? (
          <p className="text-luxury-gray text-sm text-center py-4">{t("لا نشاط بعد — التعليقات المطابقة تظهر هنا وتُحفظ كعملاء محتملين في جهات الاتصال")}</p>
        ) : (
          <div className="space-y-2">
            {activity.map((a, i) => (
              <div key={i} data-testid={`ig-activity-${i}`} className="flex items-center justify-between gap-3 bg-luxury-black border border-luxury-border rounded-lg px-4 py-2.5 text-sm flex-wrap">
                <span className="text-luxury-gold text-xs" dir="ltr">@{a.username}</span>
                <span className="text-luxury-bone flex-1 min-w-[120px] truncate">{a.text}</span>
                {a.matched_keyword && <span className="text-xs text-luxury-gray">{a.matched_keyword}</span>}
                <span className={`text-xs ${actionColor(a.action)}`}>{actionLabel(a.action)}</span>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};
